import { useState } from "react";
import emailjs from "@emailjs/browser";

function FeedbackPage({ appTheme, isMobile, setPlanToastMessage }) {
  const [feedbackType, setFeedbackType] = useState("bug");
  const [feedbackText, setFeedbackText] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [supportQuestion, setSupportQuestion] = useState("");
  const [supportSending, setSupportSending] = useState(false);

  async function handleSupportSubmit() {
    if (!supportQuestion.trim()) return;
    setSupportSending(true);
    try {
      await emailjs.send(
        "service_94y9rg6",
        "template_pg9qc1t",
        {
          feedback_type: "Support Question",
          rating: "N/A",
          message: supportQuestion.trim()
        },
        "jJbEpvrw61fmTihIC"
      );
      setSupportSending(false);
      setSupportQuestion("");
      setPlanToastMessage("✓ Message sent!");
      setTimeout(() => setPlanToastMessage(""), 2800);
    } catch (err) {
      setSupportSending(false);
    }
  }

  async function handleSubmit() {
    if (!feedbackText.trim() && rating === 0) return;

    setSending(true);
    setError("");

    try {
      await emailjs.send(
        "service_94y9rg6",
        "template_pg9qc1t",
        {
          feedback_type: feedbackType === "bug" ? "Bug Report" : "Feature Idea",
          rating: rating > 0 ? `${rating} / 5` : "Not rated",
          message: feedbackText.trim() || "No message provided"
        },
        "jJbEpvrw61fmTihIC"
      );

      setSending(false);
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        setFeedbackText("");
        setRating(0);
        setFeedbackType("bug");
      }, 2800);
    } catch (err) {
      setSending(false);
      setError("Something went wrong — please try again.");
    }
  }

  if (submitted) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "60px 20px",
          gap: "16px",
          textAlign: "center"
        }}
      >
        <div style={{ fontSize: "48px" }}>✓</div>
        <div
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: appTheme.primaryText,
            fontFamily: appTheme.fontHeading
          }}
        >
          Thanks for your feedback!
        </div>
        <div
          style={{
            fontSize: "14px",
            color: appTheme.secondaryText,
            lineHeight: 1.6,
            maxWidth: "260px"
          }}
        >
          We read every message and use it to improve Bout Time.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "14px" }}>

      {/* General support question */}
      <div style={{
        borderRadius: "18px",
        background: appTheme.cardBackground,
        border: appTheme.cardBorder,
        overflow: "hidden"
      }}>
        <div style={{
          padding: "14px 16px 10px",
          fontSize: "12px",
          fontWeight: "700",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: appTheme.secondaryText
        }}>
          Got a question?
        </div>
        <textarea
          value={supportQuestion}
          onChange={(e) => setSupportQuestion(e.target.value)}
          placeholder="Ask us anything about Bout Time and we'll get back to you..."
          rows={4}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "0 16px 14px",
            border: "none",
            background: "transparent",
            fontSize: "15px",
            color: appTheme.primaryText,
            lineHeight: 1.6,
            resize: "none",
            outline: "none",
            fontFamily: appTheme.fontBody
          }}
        />
        <div style={{
          padding: "10px 16px 14px",
          display: "flex",
          justifyContent: "flex-end"
        }}>
          <button
            onClick={handleSupportSubmit}
            disabled={!supportQuestion.trim() || supportSending}
            style={{
              padding: "10px 22px",
              borderRadius: "999px",
              border: "none",
              background: supportQuestion.trim() && !supportSending
                ? appTheme.accentColor
                : appTheme.softBackground,
              color: supportQuestion.trim() && !supportSending
                ? "#ffffff"
                : appTheme.secondaryText,
              fontWeight: "700",
              fontSize: "14px",
              cursor: supportQuestion.trim() && !supportSending
                ? "pointer"
                : "not-allowed",
              transition: "all 0.18s ease"
            }}
          >
            {supportSending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>

      {/* Divider */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "12px"
      }}>
        <div style={{ flex: 1, height: "1px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
        <div style={{ fontSize: "12px", color: appTheme.mutedText, fontWeight: "600" }}>
          OR SEND FEEDBACK
        </div>
        <div style={{ flex: 1, height: "1px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          padding: "4px",
          borderRadius: "14px",
          background: appTheme.softBackground,
          border: appTheme.cardBorder
        }}
      >
        {[
          { key: "bug", label: "Bug report" },
          { key: "feature", label: "Feature idea" }
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => {
              setFeedbackType(key);
              setFeedbackText("");
            }}
            style={{
              padding: "12px 8px",
              borderRadius: "11px",
              border: "none",
              background: feedbackType === key ? appTheme.cardBackground : "transparent",
              color: feedbackType === key ? appTheme.primaryText : appTheme.secondaryText,
              fontWeight: feedbackType === key ? "700" : "500",
              fontSize: "15px",
              cursor: "pointer",
              transition: "all 0.18s ease",
              boxShadow: feedbackType === key ? "0 2px 8px rgba(15,23,42,0.10)" : "none"
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        style={{
          borderRadius: "18px",
          background: appTheme.cardBackground,
          border: appTheme.cardBorder,
          overflow: "hidden"
        }}
      >
        <textarea
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          placeholder={
            feedbackType === "bug"
              ? "Tell us what happened — the more detail the better!"
              : "Got an idea to improve Bout Time? We'd love to hear it."
          }
          rows={7}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "16px",
            border: "none",
            background: "transparent",
            fontSize: "15px",
            color: appTheme.primaryText,
            lineHeight: 1.6,
            resize: "none",
            outline: "none",
            fontFamily: appTheme.fontBody
          }}
        />
      </div>

      <div
        style={{
          padding: "16px 18px",
          borderRadius: "18px",
          background: appTheme.cardBackground,
          border: appTheme.cardBorder
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: appTheme.primaryText,
            marginBottom: "12px"
          }}
        >
          {feedbackType === "bug"
            ? "How would you rate your experience?"
            : "How much would this feature help you?"}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                border: appTheme.cardBorder,
                background: (hoverRating || rating) >= star
                  ? "rgba(251,191,36,0.22)"
                  : appTheme.cardBackground,
                fontSize: "22px",
                cursor: "pointer",
                transition: "all 0.15s ease",
                transform: (hoverRating || rating) >= star ? "scale(1.12)" : "scale(1)"
              }}
            >
              {(hoverRating || rating) >= star ? "⭐" : "☆"}
            </button>
          ))}
        </div>

        {rating > 0 && (
          <div
            style={{
              marginTop: "10px",
              fontSize: "13px",
              color: appTheme.secondaryText
            }}
          >
            {feedbackType === "bug"
              ? ["", "Slightly annoying", "A bit frustrating", "Pretty disruptive", "Very frustrating", "Completely blocked me"][rating]
              : ["", "Nice to have", "Would use occasionally", "Would use regularly", "Really important to me", "Can't do without it"][rating]}
          </div>
        )}
      </div>

      <div
        style={{
          fontSize: "13px",
          color: appTheme.secondaryText,
          lineHeight: 1.6,
          padding: "0 4px"
        }}
      >
        To help us resolve your issue, some diagnostic information may be included with your report.
      </div>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "14px",
            background: appTheme.actionLight,
            border: appTheme.actionBorder,
            fontSize: "14px",
            color: appTheme.actionColor
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSubmit}
          disabled={(!feedbackText.trim() && rating === 0) || sending}
          style={{
            padding: "10px 22px",
            borderRadius: "999px",
            border: "none",
            background: (feedbackText.trim() || rating > 0) && !sending
              ? appTheme.accentColor
              : appTheme.softBackground,
            color: (feedbackText.trim() || rating > 0) && !sending
              ? "#ffffff"
              : appTheme.secondaryText,
            fontWeight: "700",
            fontSize: "15px",
            cursor: (feedbackText.trim() || rating > 0) && !sending
              ? "pointer"
              : "not-allowed",
            transition: "all 0.18s ease",
            minWidth: "100px"
          }}
        >
          {sending ? "Sending..." : "Submit"}
        </button>
      </div>
    </div>
  );
}

export default FeedbackPage;